export const colors = {
  green: {
    G900: '#005E51',
    G800: '##006B5D',
    G700: '#007968',
    G500: '#008674',
    G400: '#1A9282',
    G300: '#4DAA9E',
    G200: '#66B6AC',
    G100: '#80C3BA',
    G50: '#B3DBD5',
    G0: '#F0F8F7',
  },
  dark: {
    D900: '#282E2E',
    D800: '#2C3333',
    D700: '#414747',
    D600: '#565C5C',
    D500: '#6B7070',
    D400: '#808585',
    D300: '#808585',
    D200: '#C0C2C2',
    D150: '#DFE1E1',
    D100: '#EAEBEB',
    D0: '#F9F9F9',
  },
  yellow: {
    Y700: '#F0B318',
    Y600: '#F9CC59',
    Y500: '#FEDB5F',
    Y400: '#FEE68F',
    Y300: '#FFEDAF',
    Y200: '#FFF4CF',
    Y150: '#DFE1E1',
  },
  BG100: '#F6F7F2',
  red: '#D73B3B',
  /**
   * Consumer checkout CTA coral (Figma — "شراء تذكرة" / confirm buttons / total).
   *
   * `C500` is the design system's `--color-primary-500`. It was `#FF5A4D`, two steps off the Figma
   * value; corrected from the "Ticket Zone Button" spec. Only consumer-checkout surfaces read it —
   * the purchase CTA, the total, and the promo "ازالة" link — so all three move together.
   */
  coral: {
    C500: '#FF5C4F',
    C600: '#F2493D',
    C700: '#E03D32',
    C50: '#FFECEA',
  },
  /** Consumer checkout surfaces sampled from the "New Direction" Figma frames. */
  checkout: {
    /** Blush card behind the terms gate. */
    termsBg: '#FFF1EF',
    /** Neutral warm-grey bullet dot in that card (deliberately not the brand teal). */
    termsBullet: '#57534E',
  },
  /** Semantic status colors (Figma design tokens — `--color-semantic-*`). */
  semantic: {
    warning: {
      surface: '#FEF3C7',
      base: '#D97706',
      text: '#78350F',
    },
    info: {
      surface: '#DBEAFE',
      base: '#2563EB',
      text: '#1E3A8A',
    },
  },
  white: '#FFFFFF',
  offwhite: '#FCFCFD',
  /** B2B registration (Figma) — accent + chips separate from consumer green brand. */
  b2b: {
    pageBg: '#EDEDEE',
    accent: '#E4584A',
    accentHover: '#CF4D40',
    accentActive: '#B84338',
    chipSelected: '#5C4D7A',
    chipSelectedHover: '#4A3D62',
    chipInactiveBg: '#E8E8EA',
    chipInactiveBorder: '#D4D4D8',
    focusRing: '#5C4D7A',
    tagBg: '#FEE2E2',
    tagColor: '#B91C1C',
    cardBorder: '#E4E4E7',
    successIconCircle: '#FDF2F2',
    /** Footer cream (Figma reference screenshots) */
    footerBg: '#F9F8F3',
    navBarBg: '#FAF7F2',
    navBarBorder: '#E3DDD4',
    /** B2B marketing landing — hero block (pixel ref: organizer landing) */
    landingHeroBg: '#431A7A',
    /** Outlined stat / badge strokes on hero */
    landingHeroStatBorder: 'rgba(255,255,255,0.5)',
    /** Hero primary CTA (distinct from form accent) */
    landingHeroCta: '#FF5A4E',
    landingHeroCtaHover: '#F2493D',
    landingHeroCtaActive: '#E03D32',
    /** Badge star (organizer pill) */
    landingHeroStar: '#FFD700',
    landingCtaBandBg: '#FDF0EC',
    landingFeatureCardBg: '#FFFFFF',
    landingFeatureIconBg: '#EDE8F4',
    formFooterBandBg: '#F7F7F4',
    /** Invitation flow */
    inviteIconBg: '#FDDDD5',
    inviteAccentLight: '#FFA39A',
    inviteGradientFrom: '#FFE0DD',
    inviteGradientTo: '#FFF1EF',
    /** B2B registration success — filled circle + check (design ref) */
    successScreenCircle: '#2A9D54',
    /** Terms acknowledgement panel inside registration card */
    termsPanelBg: '#EEEEEB',
    /** Submit CTA (Figma coral, registration footer) */
    registrationSubmit: '#EF6E5E',
    registrationSubmitHover: '#E86252',
    registrationSubmitActive: '#DC5A4B',
    registrationCancelBorder: '#D1D1D1',
    /** {@link FieldPhone} — Iraq +964 prefix segment (Figma) */
    fieldPhonePrefixBg: '#E8E8F3',
    fieldPhonePrefixFg: '#5C4D7A',
  },
};

export const b2bFieldFocusVisible = {
  borderColor: colors.b2b.focusRing,
  boxShadow: `0 0 0 1px ${colors.b2b.focusRing}`,
};

/** Chakra v2 Checkbox: target the visual control for B2B coral accent (not consumer `green`). */
export const b2bCheckboxControlSx = {
  '& .chakra-checkbox__control': {
    borderColor: 'dark.D150',
    _checked: {
      bg: colors.b2b.accent,
      borderColor: colors.b2b.accent,
      color: 'white',
      _hover: {
        bg: colors.b2b.accentHover,
        borderColor: colors.b2b.accentHover,
      },
    },
  },
} as const;
